import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import Modal from 'react-modal';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ErrorIcon from '@mui/icons-material/Error';
import { ICancelDemandInputModel } from 'inputModels';
import { useSolicitation } from '../../../context/solicitation';
import { ContentModal, Header } from './styles';
import { ReactComponent as IconError } from '../../../assets/icons/IconCancelCircle.svg';
import { Button } from '../../Buttons/Button';
import { useApp } from '../../../context';
import { SolicitationController } from '../../../controller/SolicitationController';
import { useAlert } from '../../../hooks/useAlert';

interface ModalCancelDemandInProgressProps {
  isOpen: boolean;
  onRequestClose: () => void;
  demandId: string;
}
export function ModalCancelDemandInProgress({
  isOpen,
  demandId,
  onRequestClose,
}: ModalCancelDemandInProgressProps) {
  const alert = useAlert();
  const { setDemands } = useSolicitation();
  const { setLoading, loading } = useApp();
  const [defaultValues] = useState({
    reason: '',
  });
  const cancelSchema = yup.object().shape({
    reason: yup.string().required('O motivo é obrigatório'),
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(cancelSchema),
  });

  const handleRequestClose = () => {
    onRequestClose();
    reset();
  };

  const handleCancelDemand = (data: { reason: string }) => {
    if (!demandId) {
      alert.show({
        message: 'Não foi possível encontrar a demanda, tente novamente',
        type: 'error',
      });
      handleRequestClose();
      return;
    }
    setLoading(true);
    const update: ICancelDemandInputModel = {
      demandId,
      reason: data.reason,
    };
    SolicitationController.cancelDemandByCorrespondent(update)
      .then(() => {
        setDemands((prevDemands) => prevDemands.filter((demand) => demand.id !== demandId));
        alert.show({ message: 'Demanda cancelada com sucesso', type: 'success' });
        handleRequestClose();
      })
      .catch(() => {
        alert.show({ message: 'Erro ao cancelar demanda', type: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleRequestClose}
      overlayClassName="react-modal-overlay"
      className="react-cancel-solicitation-modal-content">
      <Header>
        <h1>
          <IconError /> Cancelar demanda
        </h1>
        <div className="close-div">
          <button type="button" onClick={handleRequestClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </Header>
      <ContentModal>
        <Controller
          control={control}
          name="reason"
          render={({ field: { value, onChange, ...rest } }) => (
            <textarea
              value={value}
              onChange={onChange}
              {...rest}
              className="note"
              placeholder="Adicione seus motivos"
            />
          )}
        />
        {errors.reason && (
          <span className="fieldError">
            <ErrorIcon fontSize="inherit" />
            {errors.reason.message}
          </span>
        )}
        <div className="content-buttons">
          <Button
            text="Cancelar"
            dimensions={{ width: '12rem', height: '2rem' }}
            font={{ size: '14' }}
            color="background_white"
            background="primary"
            backgroundHoverColor="tertiary_500"
            textHoverColor="background_white"
            onClick={loading ? () => {} : handleRequestClose}
          />
          <Button
            text="Confirmar"
            loading={loading}
            dimensions={{ width: '12rem', height: '2rem' }}
            font={{ size: '14' }}
            color="background_white"
            background="secondary"
            backgroundHoverColor="tertiary_500"
            textHoverColor="background_white"
            border={{ color: 'primary' }}
            onClick={handleSubmit(handleCancelDemand)}
          />
        </div>
      </ContentModal>
    </Modal>
  );
}
